<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useBreadcrumbsState, type BreadcrumbItem } from '@/composables/useBreadcrumbs'
import { RouterLink } from 'vue-router'

const router = useRouter()

interface BreadcrumbProps {
  /** Custom separator character */
  separator?: string
  /** Custom class for the breadcrumb container */
  containerClass?: string
  /** Custom class for the breadcrumb list */
  listClass?: string
  /** Custom class for breadcrumb items */
  itemClass?: string
  /** Custom class for the separator */
  separatorClass?: string
  /** Whether to show home icon as first item */
  showHome?: boolean
  /** Custom home label */
  homeLabel?: string
  /** Custom home icon */
  homeIcon?: string
  /** Home route name */
  homeRoute?: string
  /** Custom separator component/node */
  separatorIcon?: string
  /** Whether to hide on mobile */
  hideOnMobile?: boolean
  /** Max number of breadcrumbs to show before collapsing */
  maxItems?: number
  /** Whether to show icons */
  showIcons?: boolean
  /** Custom classes for styling */
  customClass?: string
  /** Inline styles */
  style?: string | Record<string, string>
}

const props = defineProps<BreadcrumbProps>()

const {
  separator = '/',
  containerClass = 'breadcrumb-container',
  listClass = 'breadcrumb-list',
  itemClass = 'breadcrumb-item',
  separatorClass = 'breadcrumb-separator',
  showHome = true,
  homeLabel = 'Dashboard',
  homeIcon = 'fa fa-home',
  homeRoute = 'home',
  separatorIcon = 'fa fa-chevron-right',
  hideOnMobile = false,
  maxItems = 0,
  showIcons = true,
  customClass = '',
} = props

const { breadcrumbs: computedBreadcrumbs } = useBreadcrumbsState()

/**
 * Computed breadcrumbs with optional home item
 */
const breadcrumbs = computed(() => {
  const items = computedBreadcrumbs.value
  if (!items.length) return []

  const result: BreadcrumbItem[] = []

  // Add home link if enabled and not already on home
  if (showHome && items[0]?.label !== homeLabel && items[0]?.path !== homeRoute) {
    result.push({
      label: homeLabel,
      path: homeRoute,
      icon: homeIcon,
    })
  }

  // Add route breadcrumbs
  result.push(...items)

  // Handle max items with collapse
  if (maxItems > 0 && result.length > maxItems) {
    const visibleItems = result.slice(0, maxItems - 1)
    const collapsedCount = result.length - maxItems + 1
    return [
      ...visibleItems,
      {
        label: `+${collapsedCount} more`,
        disabled: true,
        path: undefined,
      },
      result[result.length - 1],
    ]
  }

  return result
})

/**
 * Navigate to breadcrumb path
 */
function navigateTo(path: string | undefined) {
  if (path) {
    router.push(path)
  }
}

/**
 * Check if item is the last one
 */
function isLastItem(index: number): boolean {
  return index === breadcrumbs.value.length - 1
}
</script>

<template>
  <nav
    :class="[
      containerClass,
      'breadcrumb-nav',
      hideOnMobile ? 'd-none d-md-block' : '',
      customClass
    ]"
    :style="style"
    aria-label="Breadcrumb navigation"
  >
    <ol :class="[listClass, 'breadcrumb']">
      <template v-for="(item, index) in breadcrumbs" :key="index">
        <li v-if="item" :class="[itemClass, { active: item.disabled, last: isLastItem(index) }]">
          <!-- Breadcrumb link or text -->
          <template v-if="item.path && !item.disabled">
            <RouterLink
              :to="item.path"
              :class="['breadcrumb-link', itemClass + '__link']"
              @click="navigateTo(item.path)"
            >
              <i
                v-if="showIcons && item.icon"
                :class="[item.icon, 'breadcrumb-icon', 'me-1']"
                aria-hidden="true"
              ></i>
              <span class="breadcrumb-label">{{ item.label }}</span>
            </RouterLink>
          </template>
          <template v-else>
            <span
              :class="[
                'breadcrumb-label',
                itemClass + '__label',
                item.disabled ? 'breadcrumb-current' : ''
              ]"
              :aria-current="item.disabled ? 'page' : undefined"
            >
              <i
                v-if="showIcons && item.icon"
                :class="[item.icon, 'breadcrumb-icon', 'me-1']"
                aria-hidden="true"
              ></i>
              <span>{{ item.label }}</span>
            </span>
          </template>

          <!-- Separator -->
          <template v-if="!isLastItem(index)">
            <span
              :class="[separatorClass, 'breadcrumb-separator']"
              aria-hidden="true"
            >
              <i v-if="separatorIcon" :class="[separatorIcon, 'me-1']"></i>
              <span v-else>{{ separator }}</span>
            </span>
          </template>
        </li>
      </template>
    </ol>
  </nav>
</template>

<style scoped>
.breadcrumb-nav {
  padding: 0.5rem 0;
  background: transparent;
}

.breadcrumb {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: 0.875rem;
  font-weight: 500;
}

.breadcrumb-item {
  display: flex;
  align-items: center;
  color: #2D3033;
  transition: color 0.2s ease;
}

.breadcrumb-item:last-child .breadcrumb-label {
  color: #1e293b;
  font-weight: 600;
}

.breadcrumb-link {
  display: flex;
  align-items: center;
  color: #1F2021;
  text-decoration: none;
  padding: 0.25rem 0;
  transition: color 0.2s ease;
}

.breadcrumb-link:hover {
  color: #2563eb;
  text-decoration: none;
}

.breadcrumb-link:focus {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
  border-radius: 4px;
}

.breadcrumb-icon {
  font-size: 0.875rem;
  line-height: 1;
}

.breadcrumb-separator {
  display: flex;
  align-items: center;
  color: #323538;
  margin: 0 0.5rem;
  font-size: 0.5rem;
}

.breadcrumb-separator i {
  font-size: 0.625rem;
}

.breadcrumb-current {
  color: #1e293b;
  font-weight: 600;
}

/* Mobile responsive */
@media (max-width: 768px) {
  .breadcrumb-nav:not(.d-none) .breadcrumb {
    overflow-x: auto;
    padding-bottom: 0.25rem;
    -webkit-overflow-scrolling: touch;
  }

  .breadcrumb-nav:not(.d-none) .breadcrumb-item {
    flex-shrink: 0;
  }
}

/* Collapsed breadcrumbs */
.breadcrumb-item.active .breadcrumb-label[aria-current="page"] {
  font-weight: 600;
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .breadcrumb-item {
    color: #242527;
  }

  .breadcrumb-item:last-child .breadcrumb-label {
    color: #242527;
  }

  .breadcrumb-link {
    color: #242527;
  }

  .breadcrumb-link:hover {
    color: #242527;
  }

  .breadcrumb-separator {
    color: #242527;
  }

  .breadcrumb-current {
    color: #242527;
  }
}

/* Animation for breadcrumb items */
.breadcrumb-item {
  animation: breadcrumbFadeIn 0.3s ease forwards;
  opacity: 0;
}

.breadcrumb-item:nth-child(1) { animation-delay: 0ms; }
.breadcrumb-item:nth-child(2) { animation-delay: 50ms; }
.breadcrumb-item:nth-child(3) { animation-delay: 100ms; }
.breadcrumb-item:nth-child(4) { animation-delay: 150ms; }
.breadcrumb-item:nth-child(5) { animation-delay: 200ms; }
.breadcrumb-item:nth-child(6) { animation-delay: 250ms; }

@keyframes breadcrumbFadeIn {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* RTL support */
[dir="rtl"] .breadcrumb {
  flex-direction: row-reverse;
}

[dir="rtl"] .breadcrumb-separator {
  transform: rotate(180deg);
}
</style>